import { useState } from "react";
import http, { formatApiErrorDetail } from "@/api";
import { useAuth } from "@/auth";
import EvalStatusBadge from "@/components/EvalStatusBadge";
import { Send, CheckCircle2, XCircle, Upload, Undo2 } from "lucide-react";

const ACTIONS = {
  draft: [{ key: "submit", label: "Enviar a revisión", icon: Send, roles: ["admin","evaluator"], color: "var(--lala-blue)" }],
  rejected: [{ key: "submit", label: "Reenviar a revisión", icon: Send, roles: ["admin","evaluator"], color: "var(--lala-blue)" }],
  pending_approval: [
    { key: "approve", label: "Aprobar", icon: CheckCircle2, roles: ["admin"], color: "#0F5132" },
    { key: "reject", label: "Rechazar", icon: XCircle, roles: ["admin"], color: "#7F1D1D", needsNote: true },
  ],
  approved: [
    { key: "publish", label: "Publicar al proveedor", icon: Upload, roles: ["admin"], color: "var(--lala-blue)" },
    { key: "reject", label: "Devolver a borrador", icon: XCircle, roles: ["admin"], color: "#7F1D1D", needsNote: true },
  ],
  published: [
    { key: "unpublish", label: "Despublicar", icon: Undo2, roles: ["admin"], color: "#8A5A00" },
  ],
};

export default function WorkflowPanel({ evaluation, onChanged }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");

  if (!evaluation) return null;
  const actions = (ACTIONS[evaluation.status] || []).filter((a) => a.roles.includes(user?.role));
  const history = evaluation.status_history || [];

  const run = async (a) => {
    let note = "";
    if (a.needsNote) {
      note = window.prompt("Motivo / comentario:") || "";
      if (!note) return;
    }
    setBusy(a.key); setErr("");
    try {
      await http.post(`/evaluations/${evaluation.id}/${a.key}`, { note });
      onChanged?.();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(null); }
  };

  return (
    <div className="sketch-border bg-[var(--cream-soft)] p-5" data-testid="workflow-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="font-hand text-lg text-[var(--lala-blue)]">flujo de aprobación</div>
          <h3 className="font-display text-2xl flex items-center gap-2">Estado <EvalStatusBadge status={evaluation.status} size="lg" /></h3>
          <p className="text-xs text-[var(--ink-soft)] mt-1">
            Las evaluaciones publicadas son las únicas visibles para el proveedor y las únicas que cuentan en el score actual.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          {actions.length === 0 && <div className="text-xs text-[var(--ink-soft)] italic">Sin acciones disponibles para tu rol.</div>}
          {actions.map((a) => {
            const Icon = a.icon;
            return (
              <button
                key={a.key}
                onClick={() => run(a)}
                disabled={busy === a.key}
                className="sketch-pill bg-[var(--cream)] px-3 py-1.5 text-sm font-semibold inline-flex items-center gap-1.5"
                style={{ color: a.color, borderColor: a.color, boxShadow: `3px 3px 0 0 ${a.color}` }}
                data-testid={`workflow-${a.key}-btn`}
              >
                <Icon size={14} /> {a.label}
              </button>
            );
          })}
        </div>
      </div>

      {err && <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}

      {history.length > 0 && (
        <details className="mt-4" data-testid="workflow-history">
          <summary className="cursor-pointer text-sm font-semibold text-[var(--lala-blue)]">Bitácora ({history.length}) ↓</summary>
          <ol className="mt-3 space-y-2 text-xs">
            {history.slice().reverse().map((h, i) => (
              <li key={i} className="flex gap-2">
                <EvalStatusBadge status={h.status} />
                <div className="flex-1">
                  <div className="text-[var(--ink-soft)]">{new Date(h.at).toLocaleString("es-MX")} · {h.by}</div>
                  {h.note && <div className="italic">"{h.note}"</div>}
                </div>
              </li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
}
